import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Dialog,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import {
  FileText,
  FileUp,
  History,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Wand2,
  ChevronDown,
  Clock,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  useEditor,
  EditorContent,
} from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import {
  apiCreateDocumentTemplate,
  apiCreateDocumentTemplateFile,
  apiDeleteDocumentTemplate,
  apiGenerateDocumentFromTemplate,
  apiPreviewDocumentTemplate,
  apiGetDocumentTemplates,
  apiGetDocumentTemplateVersions,
  apiGetEmployees,
  apiUpdateDocumentTemplate,
  type DocumentTemplate,
  type DocumentTemplateType,
  type DocumentTemplateVersion,
} from "@/lib/api";
import { TEMPLATE_TYPES } from "./TemplateFormModal";
import { DocumentTemplateEditor } from "./DocumentTemplateEditor";

// ── Helpers ────────────────────────────────────────────────────────────────────

function getTemplateTypeLabel(
  t: TFunction,
  type: string,
) {
  const item = TEMPLATE_TYPES.find((templateType) => templateType.value === type);
  return item ? t(item.labelKey) : type;
}

// ── TypeBadge ──────────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: string }) {
  const { t } = useTranslation();
  const colorMap: Record<string, string> = {
    contract: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    annex: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    bank: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    court: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    sick_leave: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
    custom: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        colorMap[type] ?? colorMap.custom
      }`}
    >
      {getTemplateTypeLabel(t, type)}
    </span>
  );
}

// ── UseTemplateDialog — WYSIWYG editor with resolved placeholders ──────────────

function UseTemplateDialog({
  template,
  onClose,
}: {
  template: DocumentTemplate;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [employeeId, setEmployeeId] = useState<string>("");
  const [documentName, setDocumentName] = useState(template.name);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
  });

  // Tiptap editor instance
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      FontSize,
      Highlight.configure({ multicolor: false }),
    ],
    content: template.contentText ?? "<p></p>",
    editorProps: {
      attributes: {
        class: "outline-none min-h-[400px] leading-relaxed text-foreground px-6 py-4",
        style: "font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 12pt;",
        spellcheck: "true",
      },
    },
  });

  // When employee changes, fetch resolved content
  const previewMutation = useMutation({
    mutationFn: (empId: number) => apiPreviewDocumentTemplate(template.id, empId),
    onSuccess: (data) => {
      editor?.commands.setContent(data.content);
      setDocumentName(`${template.name} — ${data.employeeName}`);
      setPreviewLoaded(true);
    },
    onError: (err: any) => {
      console.error("[document template preview] failed", err);
      toast.error(t("documents.templates.previewFailed"));
    },
  });

  const handleEmployeeChange = (val: string) => {
    setEmployeeId(val);
    setPreviewLoaded(false);
    previewMutation.mutate(Number(val));
  };

  const generateMutation = useMutation({
    mutationFn: () =>
      apiGenerateDocumentFromTemplate(template.id, {
        employeeId: Number(employeeId),
        content: editor?.getHTML() ?? "",
        documentName: documentName.trim() || template.name,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["hr-documents"] });
      toast.success(t("documents.templates.documentSaved"));
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-full h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            {t("documents.templates.generateTitle")}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t("documents.templates.templateLabel")}: <span className="font-medium text-foreground">{template.name}</span>
          </p>
        </DialogHeader>

        {/* Controls row */}
        <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center gap-4 shrink-0 flex-wrap">
          <div className="flex-1 min-w-52">
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              {t("documents.templates.employeeLabel")} *
            </label>
            <EmployeeCombobox
              employees={(employees as any[])}
              value={employeeId}
              onChange={handleEmployeeChange}
              placeholder={t("documents.templates.selectEmployee")}
            />
          </div>
          <div className="flex-1 min-w-52">
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              {t("documents.templates.documentNameLabel")}
            </label>
            <Input
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className="h-8 text-sm"
              placeholder={t("documents.templates.documentNamePlaceholder")}
            />
          </div>
          {previewMutation.isPending && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("documents.templates.resolvingPlaceholders")}
            </div>
          )}
          {previewLoaded && !previewMutation.isPending && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-4">
              ✓ {t("documents.templates.placeholdersResolved")}
            </p>
          )}
        </div>

        {/* Editor area */}
        <div className="flex-1 overflow-y-auto bg-background">
          {!editor ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <EditorContent editor={editor} />
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 py-3 border-t border-border shrink-0">
          <p className="text-xs text-muted-foreground mr-auto self-center">
            {t("documents.templates.saveLocation")}
          </p>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!employeeId || generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
          >
            {generateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t("documents.templates.saveDocument")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── VersionHistoryDialog ───────────────────────────────────────────────────────

function VersionHistoryDialog({
  template,
  onClose,
}: {
  template: DocumentTemplate;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "mk" ? "mk-MK" : "en-GB";

  const { data: versions = [], isLoading } = useQuery<DocumentTemplateVersion[]>({
    queryKey: ["document-template-versions", template.id],
    queryFn: () => apiGetDocumentTemplateVersions(template.id),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-full max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t("documents.templates.versionHistory")}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{template.name}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && versions.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {t("documents.templates.noVersions")}
              </p>
            </div>
          )}
          {!isLoading && versions.length > 0 && (
            <div className="space-y-3">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="rounded-lg border border-border/60 bg-card p-3 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t("documents.templates.versionNumber", { number: v.version_number })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(v.changed_at).toLocaleString(locale)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.template_type && <TypeBadge type={v.template_type} />}
                    {v.changed_by_name && (
                      <span className="text-xs text-muted-foreground">
                        {t("documents.templates.changedBy", { name: v.changed_by_name })}
                      </span>
                    )}
                  </div>
                  {v.name !== template.name && (
                    <p className="text-xs text-muted-foreground">
                      {t("documents.templates.previousName", { name: v.name })}
                    </p>
                  )}
                  {v.change_note && (
                    <p className="text-xs text-muted-foreground italic">"{v.change_note}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose}>{t("documents.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── EditTemplateDialog ─────────────────────────────────────────────────────────

function TemplateEditorOverlay({
  template,
  onClose,
}: {
  template: DocumentTemplate | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: { name: string; templateType: DocumentTemplateType; contentText: string }) =>
      apiCreateDocumentTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateCreated"));
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; templateType: DocumentTemplateType; contentText: string }) =>
      apiUpdateDocumentTemplate(template!.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document-templates"] });
      qc.invalidateQueries({ queryKey: ["document-template-versions", template?.id] });
      toast.success(t("documents.messages.templateUpdated"));
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = (data: { name: string; templateType: DocumentTemplateType; contentText: string }) => {
    if (template) updateMutation.mutate(data);
    else createMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <DocumentTemplateEditor
        template={template}
        onSave={handleSave}
        onCancel={onClose}
        isSaving={isSaving}
      />
    </div>
  );
}

// ── PdfUploadDialog ────────────────────────────────────────────────────────────

function PdfUploadDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState<DocumentTemplateType>("contract");
  const [file, setFile] = useState<File | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiCreateDocumentTemplateFile(name.trim(), templateType, file!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateCreated"));
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-4 w-4" />
            {t("documents.templates.uploadPdfTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t("documents.templates.nameColumn")} <span className="text-destructive">*</span>
            </label>
            <Input
              placeholder={t("documents.templates.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("documents.templates.typeLabel")}</label>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value as DocumentTemplateType)}
            >
              {TEMPLATE_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t("documents.templates.fileLabel")} <span className="text-destructive">*</span>
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/50 transition-colors">
              {file ? (
                <p className="text-sm font-medium">{file.name}</p>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">{t("documents.templates.filePlaceholder")}</p>
                </>
              )}
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            disabled={!name.trim() || !file || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("documents.templates.uploadTemplate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main tab ───────────────────────────────────────────────────────────────────

export function DocumentTemplatesTab() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language === "mk" ? "mk-MK" : "en-GB";

  const [editTarget, setEditTarget] = useState<DocumentTemplate | null | "new-wysiwyg">(null);
  const [uploadPdfOpen, setUploadPdfOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentTemplate | null>(null);
  const [useTarget, setUseTarget] = useState<DocumentTemplate | null>(null);
  const [historyTarget, setHistoryTarget] = useState<DocumentTemplate | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["document-templates"],
    queryFn: apiGetDocumentTemplates,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteDocumentTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateDeleted"));
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err.message);
      setDeleteTarget(null);
    },
  });

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t("documents.templates.title")}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("documents.templates.subtitle")}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" />
              {t("documents.templates.newTemplate")}
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditTarget("new-wysiwyg")}>
              <FileText className="h-4 w-4 mr-2" />
              {t("documents.templates.createWithEditor")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setUploadPdfOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              {t("documents.templates.uploadPdf")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Placeholder hint ── */}
      <div className="rounded-lg border border-border/60 bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
        <strong className="text-foreground">{t("documents.templates.availablePlaceholders")}: </strong>
        {["{employee_name}", "{start_date}", "{position}", "{salary}", "{company_name}", "{today_date}"].map(
          (ph) => (
            <code
              key={ph}
              className="bg-background border border-border rounded px-1 py-0.5 mr-1"
            >
              {ph}
            </code>
          ),
        )}
      </div>

      {/* ── Table ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && templates.length === 0 && (
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {t("documents.templates.emptyTitle")}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("documents.templates.emptyDescription")}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditTarget("new-wysiwyg")}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("documents.templates.createAction")}
          </Button>
        </div>
      )}

      {!isLoading && templates.length > 0 && (
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("documents.templates.nameColumn")}</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">{t("documents.templates.authorColumn")}</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">{t("documents.templates.createdColumn")}</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">{t("documents.templates.lastUpdatedColumn")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {templates.map((tpl) => (
                <tr
                  key={tpl.id}
                  className="hover:bg-muted/20 transition-colors group cursor-pointer"
                  onClick={() => tpl.contentText ? setEditTarget(tpl) : setUseTarget(tpl)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        {tpl.fileKey && !tpl.contentText ? (
                          <FileUp className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <FileText className="h-3.5 w-3.5 text-primary" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">{tpl.name}</span>
                      <TypeBadge type={tpl.templateType} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                    {tpl.createdByName ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                    {new Date(tpl.createdAt).toLocaleDateString(locale)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                    {tpl.updatedAt
                      ? new Date(tpl.updatedAt).toLocaleDateString(locale)
                      : "—"}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1 text-destructive hover:text-destructive"
                        title={t("documents.templates.deleteAction")}
                        onClick={() => setDeleteTarget(tpl)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Use (Generate) Dialog ── */}
      {useTarget && (
        <UseTemplateDialog
          template={useTarget}
          onClose={() => setUseTarget(null)}
        />
      )}

      {/* ── Edit / Create full-screen overlay ── */}
      {editTarget !== null && (
        <TemplateEditorOverlay
          template={editTarget === "new-wysiwyg" ? null : editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* ── PDF Upload Dialog ── */}
      {uploadPdfOpen && (
        <PdfUploadDialog onClose={() => setUploadPdfOpen(false)} />
      )}

      {/* ── Version History Dialog ── */}
      {historyTarget && (
        <VersionHistoryDialog
          template={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {/* ── Delete Confirm ── */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("documents.templates.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("documents.templates.deleteDescription", {
                name: deleteTarget?.name ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
